import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import usePageTitle from '../../hooks/usePageTitle';
import '../../styles/fonts.css';
import '../../styles/tools.css';

const DRIVER_COLORS = ['#e3b341', '#58a6ff', '#3fb950', '#f78166', '#d2a8ff', '#79c0ff'];

const inp = {
  background: '#21262d',
  border: '1px solid #30363d',
  borderRadius: '6px',
  color: '#e6edf3',
  padding: '8px 12px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

const lbl = { fontSize: '12px', color: '#8b949e', marginBottom: '4px', display: 'block' };

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {label && <label style={lbl}>{label}</label>}
      {children}
    </div>
  );
}

// Add minutes to a HH:MM string, returns HH:MM (wraps past midnight)
function addMins(timeStr, mins) {
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + m + Math.round(mins);
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

function fmtMins(mins) {
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
  return `${m}min`;
}

function generateStints(totalMins, stintMins, pitMins, numDrivers) {
  const stints = [];
  let currentTime = 0;
  let idx = 0;
  while (currentTime < totalMins) {
    const timeAfterStintAndPit = currentTime + stintMins + pitMins;
    const isLast = timeAfterStintAndPit >= totalMins;
    const end = isLast ? totalMins : currentTime + stintMins;
    stints.push({
      num: idx + 1,
      driver: idx % numDrivers,
      startMins: currentTime,
      endMins: end,
      durationMins: end - currentTime,
    });
    if (isLast) break;
    currentTime = end + pitMins;
    idx++;
  }
  return stints;
}

// Build timeline segments: alternating drive/pit blocks
function buildTimeline(stints, pitMins) {
  const segments = [];
  for (let i = 0; i < stints.length; i++) {
    segments.push({ type: 'drive', driver: stints[i].driver, durationMins: stints[i].durationMins });
    if (i < stints.length - 1) {
      segments.push({ type: 'pit', durationMins: pitMins });
    }
  }
  return segments;
}

function EndurancePlanner() {
  const { t } = useTranslation();
  usePageTitle(t('tools.endurance.title'));

  const [raceH, setRaceH] = useState('');
  const [raceM, setRaceM] = useState('');
  const [startTime, setStartTime] = useState('14:00');
  const [numDrivers, setNumDrivers] = useState(2);
  const [drivers, setDrivers] = useState([
    { name: '', color: DRIVER_COLORS[0] },
    { name: '', color: DRIVER_COLORS[1] },
  ]);
  const [stintMins, setStintMins] = useState('');
  const [pitMins, setPitMins] = useState('5');
  const [minDriveH, setMinDriveH] = useState('');
  const [minDriveM, setMinDriveM] = useState('');
  const [plan, setPlan] = useState(null);

  const handleNumDriversChange = useCallback((n) => {
    setNumDrivers(n);
    setDrivers(prev => {
      const next = [...prev];
      while (next.length < n) next.push({ name: '', color: DRIVER_COLORS[next.length % DRIVER_COLORS.length] });
      return next.slice(0, n);
    });
  }, []);

  const updateDriver = (i, field, value) => {
    setDrivers(prev => prev.map((d, idx) => idx === i ? { ...d, [field]: value } : d));
  };

  const handleGenerate = () => {
    const totalMins = parseInt(raceH || 0) * 60 + parseInt(raceM || 0);
    const stint = parseFloat(stintMins);
    const pit = parseFloat(pitMins || 0);
    if (!totalMins || !stint || totalMins <= 0 || stint <= 0) return;
    const stints = generateStints(totalMins, stint, pit, numDrivers);
    const minDriveMins = parseInt(minDriveH || 0) * 60 + parseInt(minDriveM || 0);
    setPlan({ stints, totalMins, stintMins: stint, pitMins: pit, minDriveMins: minDriveMins || null });
  };

  const driverNames = drivers.map((d, i) => d.name || `${t('tools.endurance.driverDefault')} ${i + 1}`);
  const driverColors = drivers.map(d => d.color);

  const driverSummary = useMemo(() => {
    if (!plan) return [];
    return drivers.slice(0, numDrivers).map((_, i) => {
      const myStints = plan.stints.filter(s => s.driver === i);
      const totalTime = myStints.reduce((a, s) => a + s.durationMins, 0);
      const meetsMin = plan.minDriveMins ? totalTime >= plan.minDriveMins : null;
      return { totalTime, numStints: myStints.length, meetsMin };
    });
  }, [plan, drivers, numDrivers]);

  const canGenerate = (parseInt(raceH || 0) * 60 + parseInt(raceM || 0)) > 0 && parseFloat(stintMins) > 0;
  const timeline = plan ? buildTimeline(plan.stints, plan.pitMins) : null;

  return (
    <div style={{ minHeight: '100vh', background: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <main style={{ flex: 1, maxWidth: '860px', margin: '0 auto', padding: '48px 24px', color: '#c9d1d9' }}>

        <div className="no-print" style={{ marginBottom: '12px' }}>
          <Link to="/tools" style={{ fontSize: '13px', color: '#8b949e', textDecoration: 'none' }}>
            ← {t('tools.pageTitle')}
          </Link>
        </div>
        <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>
          <i className="fas fa-stopwatch" style={{ color: '#58a6ff', marginRight: '10px' }} />
          {t('tools.endurance.title')}
        </h1>
        <p style={{ fontSize: '13px', color: '#8b949e', marginBottom: '32px' }}>
          {t('tools.endurance.desc')}
        </p>

        {/* ── Form ── */}
        <div className="no-print" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Race duration + start time */}
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <div>
              <label style={lbl}>{t('tools.endurance.raceDuration')}</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="number" min="0" value={raceH} onChange={e => setRaceH(e.target.value)} placeholder="0" style={{ ...inp, width: '64px' }} />
                <span style={{ color: '#8b949e', fontSize: '13px' }}>h</span>
                <input type="number" min="0" max="59" value={raceM} onChange={e => setRaceM(e.target.value)} placeholder="0" style={{ ...inp, width: '64px' }} />
                <span style={{ color: '#8b949e', fontSize: '13px' }}>min</span>
              </div>
            </div>
            <Field label={t('tools.endurance.raceStart')}>
              <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={{ ...inp, width: '120px', colorScheme: 'dark' }} />
            </Field>
          </div>

          {/* Stint + pit */}
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <Field label={`${t('tools.endurance.stintDuration')} (min)`}>
              <input type="number" min="1" value={stintMins} onChange={e => setStintMins(e.target.value)} placeholder="ex: 60" style={{ ...inp, width: '100px' }} />
            </Field>
            <Field label={`${t('tools.endurance.pitTime')} (min)`}>
              <input type="number" min="0" step="0.5" value={pitMins} onChange={e => setPitMins(e.target.value)} placeholder="5" style={{ ...inp, width: '100px' }} />
            </Field>
          </div>

          {/* Min drive time */}
          <div>
            <label style={lbl}>{t('tools.endurance.minDriveTime')}</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="number" min="0" value={minDriveH} onChange={e => setMinDriveH(e.target.value)} placeholder="0" style={{ ...inp, width: '64px' }} />
              <span style={{ color: '#8b949e', fontSize: '13px' }}>h</span>
              <input type="number" min="0" max="59" value={minDriveM} onChange={e => setMinDriveM(e.target.value)} placeholder="0" style={{ ...inp, width: '64px' }} />
              <span style={{ color: '#8b949e', fontSize: '13px' }}>min</span>
            </div>
          </div>

          {/* Number of drivers */}
          <div>
            <label style={lbl}>{t('tools.endurance.numDrivers')}</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[1, 2, 3, 4, 5, 6].map(n => (
                <button key={n} onClick={() => handleNumDriversChange(n)} style={{
                  width: '40px', height: '40px', borderRadius: '6px', fontSize: '15px', fontWeight: 700,
                  border: '1px solid', cursor: 'pointer',
                  borderColor: numDrivers === n ? '#58a6ff' : '#30363d',
                  background: numDrivers === n ? 'rgba(88,166,255,0.12)' : '#21262d',
                  color: numDrivers === n ? '#58a6ff' : '#8b949e',
                }}>{n}</button>
              ))}
            </div>
          </div>

          {/* Driver names + colors */}
          <div>
            <label style={lbl}>{t('tools.endurance.driverNames')}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {drivers.slice(0, numDrivers).map((d, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <div style={{
                    width: '12px', height: '12px', borderRadius: '50%',
                    background: d.color, flexShrink: 0, cursor: 'pointer',
                    position: 'relative',
                  }}>
                    <input
                      type="color"
                      value={d.color}
                      onChange={e => updateDriver(i, 'color', e.target.value)}
                      style={{
                        opacity: 0, position: 'absolute', inset: 0,
                        width: '100%', height: '100%', cursor: 'pointer',
                      }}
                      title={t('tools.endurance.changeColor')}
                    />
                  </div>
                  <input
                    type="text"
                    value={d.name}
                    onChange={e => updateDriver(i, 'name', e.target.value)}
                    placeholder={`${t('tools.endurance.driverDefault')} ${i + 1}`}
                    style={{ ...inp, width: '200px' }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Generate button */}
          <div>
            <button
              onClick={handleGenerate}
              disabled={!canGenerate}
              style={{
                padding: '10px 28px',
                background: canGenerate ? '#238636' : '#2d3543',
                color: canGenerate ? '#fff' : '#8b949e',
                border: 'none', borderRadius: '6px',
                fontSize: '14px', fontWeight: 600,
                cursor: canGenerate ? 'pointer' : 'not-allowed',
                transition: 'background 0.2s',
              }}
            >
              {t('tools.endurance.generateBtn')}
            </button>
          </div>
        </div>

        {/* ── Plan output ── */}
        {plan && (
          <div style={{ marginTop: '40px' }}>

            {/* Print header (only visible when printing) */}
            <div className="tools-plan-header" style={{ display: 'none' }}>
              <h2 style={{ fontSize: '18px', marginBottom: '4px' }}>{t('tools.endurance.planTitle')}</h2>
              <p style={{ fontSize: '12px', color: '#666' }}>
                {t('tools.endurance.start')}: {startTime} · {t('tools.endurance.raceDuration')}: {fmtMins(plan.totalMins)}
              </p>
            </div>

            {/* Section header + print button */}
            <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#e6edf3', margin: 0 }}>
                {t('tools.endurance.planTitle')}
              </h2>
              <button
                onClick={() => window.print()}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', borderRadius: '6px',
                  background: '#21262d', border: '1px solid #30363d',
                  color: '#c9d1d9', fontSize: '13px', fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <i className="fas fa-print" /> {t('tools.endurance.printBtn')}
              </button>
            </div>

            {/* Timeline */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ fontSize: '12px', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('tools.endurance.timeline')}
              </div>
              <div className="tools-timeline-bar" style={{
                display: 'flex', width: '100%', height: '36px',
                borderRadius: '6px', overflow: 'hidden',
                border: '1px solid #30363d',
              }}>
                {timeline.map((seg, i) => (
                  <div
                    key={i}
                    title={seg.type === 'pit'
                      ? `${t('tools.endurance.pitStop')} (${fmtMins(seg.durationMins)})`
                      : `${driverNames[seg.driver]} — ${fmtMins(seg.durationMins)}`
                    }
                    style={{
                      width: `${(seg.durationMins / plan.totalMins) * 100}%`,
                      background: seg.type === 'pit' ? '#2d313f' : driverColors[seg.driver],
                      flexShrink: 0,
                      transition: 'opacity 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.8'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                  />
                ))}
              </div>

              {/* Legend */}
              <div style={{ display: 'flex', gap: '16px', marginTop: '10px', flexWrap: 'wrap' }}>
                {drivers.slice(0, numDrivers).map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: d.color, flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: '#c9d1d9' }}>{driverNames[i]}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#2d313f', border: '1px solid #30363d', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: '#8b949e' }}>{t('tools.endurance.pitStop')}</span>
                </div>
              </div>
            </div>

            {/* Stint table */}
            <div style={{ marginBottom: '28px', overflowX: 'auto' }}>
              <table className="tools-stint-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #30363d' }}>
                    {['stintNum', 'driver', 'start', 'end', 'duration'].map(k => (
                      <th key={k} style={{ padding: '8px 12px', textAlign: 'left', color: '#8b949e', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {t(`tools.endurance.col_${k}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {plan.stints.map((s, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #21262d' }}>
                      <td style={{ padding: '10px 12px', color: '#8b949e' }}>{s.num}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: driverColors[s.driver], flexShrink: 0 }} />
                          <span style={{ color: '#e6edf3', fontWeight: 600 }}>{driverNames[s.driver]}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#c9d1d9', fontFamily: 'monospace' }}>
                        {addMins(startTime, s.startMins)}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#c9d1d9', fontFamily: 'monospace' }}>
                        {addMins(startTime, s.endMins)}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#c9d1d9' }}>
                        {fmtMins(s.durationMins)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Driver summary */}
            <div>
              <div style={{ fontSize: '12px', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('tools.endurance.summary')}
              </div>
              <table className="tools-summary-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #30363d' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#8b949e', fontWeight: 600 }}>{t('tools.endurance.driver')}</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#8b949e', fontWeight: 600 }}>{t('tools.endurance.col_stintNum')}</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#8b949e', fontWeight: 600 }}>{t('tools.endurance.totalDrive')}</th>
                    {plan.minDriveMins && (
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: '#8b949e', fontWeight: 600 }}>{t('tools.endurance.meetsMin')}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {driverSummary.map((s, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #21262d' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: driverColors[i], flexShrink: 0 }} />
                          <span style={{ color: '#e6edf3', fontWeight: 600 }}>{driverNames[i]}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#c9d1d9' }}>{s.numStints}</td>
                      <td style={{ padding: '10px 12px', color: '#c9d1d9' }}>{fmtMins(s.totalTime)}</td>
                      {plan.minDriveMins && (
                        <td style={{ padding: '10px 12px' }}>
                          {s.meetsMin
                            ? <span style={{ color: '#3fb950', fontWeight: 600 }}>✓ {t('tools.endurance.yes')}</span>
                            : <span style={{ color: '#f85149', fontWeight: 600 }}>✗ {t('tools.endurance.no')}</span>
                          }
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        )}

      </main>
      <Footer />
    </div>
  );
}

export default EndurancePlanner;
